/**
 * RSVP button shown right under an Action's details, in both the feed card
 * and the detail page (PostCard renders both). Logged-out visitors get a
 * Log in / Sign up choice instead — the app can't tell "has an account but
 * isn't logged in" from "never signed up" without asking — both carrying
 * ?from= back to this Action's own permalink regardless of where the click
 * happened, so the return-to-Action flow is identical from feed or detail.
 */
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import type { ActionPost } from '@olimhub/shared'
import { actionsApi } from '@/api'
import { useAuthStore } from '@/store/auth.store'
import { useToast } from '@/components/common/Toast'

const PILL_BUTTON: React.CSSProperties = {
  padding: '9px 18px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  fontWeight: 700,
  fontSize: 'var(--fs-sm)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
}

// Calendar-date comparison (local time), not exact time — an Action still
// accepts RSVPs for its whole day, only closes the day after. Mirrors the
// server-side check in actions.service.ts rsvp() (UTC there; this is a UX
// convenience, the server is the real gate).
function isPastDate(iso: string): boolean {
  const start = new Date(iso)
  start.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return start.getTime() < today.getTime()
}

interface ActionRsvpButtonProps {
  post: ActionPost
}

export function ActionRsvpButton({ post }: ActionRsvpButtonProps) {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const action = post.action
  const from = encodeURIComponent(`/posts/${post.id}`)
  const isPast = isPastDate(action.startsAt)

  const rsvpMutation = useMutation({
    mutationFn: () => (action.viewerRsvp ? actionsApi.cancelRsvp(post.id) : actionsApi.rsvp(post.id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['post', post.id] })
      void queryClient.invalidateQueries({ queryKey: ['feed'] })
      void queryClient.invalidateQueries({ queryKey: ['group-feed'] })
    },
    onError: () => toast.error('Something went wrong. Please try again.'),
  })

  if (isPast) {
    return (
      <div style={{ margin: '10px 16px 0', display: 'flex', justifyContent: 'center' }}>
        <span style={{ ...PILL_BUTTON, background: 'var(--surface-2)', color: 'var(--text-faint)', cursor: 'default' }}>
          {t('actions.rsvp_closed')}
        </span>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', gap: 8, margin: '10px 16px 0' }}>
        <button
          type="button"
          onClick={() => navigate(`/login?from=${from}`)}
          style={{ ...PILL_BUTTON, flex: 1, background: 'var(--action)', color: '#fff' }}
        >
          {t('auth.to_login_cta')}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/signup?from=${from}`)}
          style={{ ...PILL_BUTTON, flex: 1, background: 'var(--surface-2)', color: 'var(--text-strong)' }}
        >
          {t('auth.to_signup_cta')}
        </button>
      </div>
    )
  }

  return (
    <div style={{ margin: '10px 16px 0', display: 'flex', justifyContent: 'center' }}>
      <button
        type="button"
        disabled={rsvpMutation.isPending}
        onClick={() => rsvpMutation.mutate()}
        style={{
          ...PILL_BUTTON,
          background: action.viewerRsvp ? 'var(--surface-2)' : 'var(--action)',
          color: action.viewerRsvp ? 'var(--text-strong)' : '#fff',
          opacity: rsvpMutation.isPending ? 0.7 : 1,
        }}
      >
        {rsvpMutation.isPending && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
        {action.viewerRsvp ? t('actions.rsvp_cancel') : t('actions.rsvp_confirm')}
      </button>
    </div>
  )
}
